import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import dayjs from 'dayjs';

import { temperatureContext } from '../../../lib/context';

const timeFormat = 'YYYY-MM-DD HH:mm';

const TabButton = ({ label, active, onClick, isLeft = false }: any) => (
    <button
        className={`flex-1 h-[50px] min-w-0 text-sm font-semibold border-b-[3px] transition-colors text-center focus:outline-none focus-visible:outline-none focus:ring-0 active:outline-none flex items-center justify-center ${active ? 'text-[#3F8EE8] border-[#3F8EE8]' : 'text-[#D4D4D4] border-transparent'} ${isLeft ? 'shadow-[2px_0px_2px_0px_#0000000A]' : ''}`}
        onClick={onClick}
        type='button'
    >
        {label}
    </button>
);

const InfoColumnItem = ({ label, value }: any) => (
    <div className='flex flex-col text-xs'>
        <span className='text-black font-[400]'>{label}</span>
        <span className='text-[#757575]'>{value || '--'}</span>
    </div>
);

const PageHeader = ({ label }: any) => (
    <div
        className='flex items-center justify-center h-[50px] text-sm font-semibold text-[#3F8EE8]'
        style={{
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.13), inset 0px 2px 2px rgba(255, 255, 255, 0.61)',
            borderBottom: '3px solid #3F8EE8',
        }}
    >
        {label}
    </div>
);

function timeInRange(time: any, start: any, end: any) {
    const format = 'HH:mm';
    const timeObj = dayjs(time).format(format);
    const startObj = dayjs(start).format(format);
    const endObj = dayjs(end).format(format);
    if (startObj <= endObj) {
        return timeObj >= startObj && timeObj <= endObj;
    }
    return timeObj >= startObj || timeObj <= endObj;
}

function MobileRecordDrawer(props: any) {
    const {
        tKey,
        row,
        metaData,
        threshold,
        notifyEmails,
        details,
        minTemp,
        maxTemp,
        loading,
        activeTab,
        onTabChange,
        pageIndex,
        onPageChange,
        onEditNote,
        onSaveNote,
        editNote,
        noteInput,
        intervalValue,
        ro,
    } = props;
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const tempUnit = useContext(temperatureContext);
    const [touchStart, setTouchStart] = useState(null);

    const alertStart = threshold?.alert_start_time ?? dayjs(row.startTime).startOf('day');
    const alertEnd = threshold?.alert_end_time ?? dayjs(row.endTime).endOf('day');

    const noteContent = editNote ? (
        <div className='flex items-center gap-2 mt-2'>
            <input ref={noteInput} defaultValue={row.note} className='flex-1 border rounded-full px-3 py-1 text-sm' />
            <button className='px-4 py-1 rounded-full bg-[#2F7DE1] text-white text-sm' onClick={onSaveNote}>
                {t('confirm')}
            </button>
        </div>
    ) : (
        <div className='flex items-center justify-between mt-2 text-sm'>
            <span className='text-[#2F2F2F]'>{row.note || t('note')}</span>
            <IconButton
                size='small'
                onClick={onEditNote}
                sx={{
                    backgroundColor: '#3F8EE8',
                    color: '#fff',
                    padding: '6px',
                    '&:hover': { backgroundColor: '#2364b5' },
                }}
            >
                <EditIcon fontSize='inherit' />
            </IconButton>
        </div>
    );

    const chartData = useMemo(() => {
        if (!details || details.length === 0) {
            return { data: [] as any[], layout: {} };
        }
        const normal = details.filter(
            (item: any) =>
                !timeInRange(item.time, alertStart, alertEnd) ||
                (item.temperature <= row.upperLimit && item.temperature >= row.lowerLimit)
        );
        const abnormal = details.filter(
            (item: any) =>
                timeInRange(item.time, alertStart, alertEnd) &&
                (item.temperature > row.upperLimit || item.temperature < row.lowerLimit)
        );
        return {
            data: [
                {
                    x: details.map((item: any) => item.time),
                    y: details.map((item: any) => item.temperature),
                    type: 'scatter',
                    mode: 'lines',
                    marker: { color: '#E6E6FF' },
                    showlegend: false,
                },
                {
                    x: normal.map((item: any) => item.time),
                    y: normal.map((item: any) => item.temperature),
                    type: 'scatter',
                    mode: 'markers',
                    marker: { color: '#3F8EE8' },
                    name: t('normal'),
                },
                {
                    x: abnormal.map((item: any) => item.time),
                    y: abnormal.map((item: any) => item.temperature),
                    type: 'scatter',
                    mode: 'markers',
                    marker: { color: '#FF3838' },
                    name: t('abnormal'),
                },
            ],
            layout: {
                yaxis: { range: [minTemp - 5, maxTemp + 5], fixedrange: true },
                xaxis: { fixedrange: true },
                height: 300,
                margin: { l: 30, r: 10, t: 10, b: 30 },
                paper_bgcolor: '#FFFFFF',
                plot_bgcolor: '#FFFFFF',
                legend: { orientation: 'v', x: 1, xanchor: 'right', y: 1, yanchor: 'top' },
            },
        };
    }, [details, minTemp, maxTemp, row.lowerLimit, row.upperLimit, alertStart, alertEnd, t('abnormal'), t('normal')]);

    const leftInfoItems = [
        { label: t('Edit.numberOfRecords'), value: row.length },
        { label: t('Edit.shipmentNumber'), value: metaData?.shipment_number },
        { label: t('Edit.carrier'), value: metaData?.carrier_name },
        { label: t('Edit.startLocation'), value: metaData?.start_location },
    ];

    const rightInfoItems = [
        { label: t('duration'), value: row.duration },
        { label: t('Edit.shipper'), value: metaData?.shipper_name },
        { label: t('Edit.consignee'), value: metaData?.consignee_name },
        { label: t('Edit.endLocation'), value: metaData?.end_location },
    ];

    const leftConfigureItems = [
        { label: t('Edit.excursionType'), value: threshold?.type },
        {
            label: t('Edit.lowerLimit'),
            value:
                threshold?.lower_limit !== undefined
                    ? `${threshold.lower_limit}°${tempUnit === 'fahrenheit' ? 'F' : 'C'}`
                    : '--',
        },
        {
            label: t('Edit.loggingInterval'),
            value: intervalValue ? `${Math.round(intervalValue / 60)}m` : '--',
        },
        {
            label: t('Edit.alarmNotificationEmail'),
            value: notifyEmails && notifyEmails.length > 0 ? notifyEmails.join(', ') : '--',
        },
    ];

    const rightConfigureItems = [
        {
            label: t('Edit.upperLimit'),
            value:
                threshold?.upper_limit !== undefined
                    ? `${threshold.upper_limit}°${tempUnit === 'fahrenheit' ? 'F' : 'C'}`
                    : '--',
        },
        {
            label: t('duration'),
            value: threshold?.time_limit ? `${Math.round(threshold.time_limit / 60)}m` : '--',
        },
    ];

    const onEditRow = props.onEditRow;

    const renderInfo = () => (
        <div className='rounded-2xl bg-[#F5F7FB] min-h-[260px] flex flex-col p-2 pl-8'>
            <div className='text-base font-semibold text-[#2F2F2F]'>{t('Edit.information')}</div>
            <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-3 flex-1'>
                <div className='flex flex-col space-y-2 items-start'>
                    {leftInfoItems.map((item: any) => (
                        <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                    ))}
                </div>
                <div className='flex flex-col space-y-2 items-start h-full'>
                    <div className='flex flex-col space-y-2 items-start justify-start w-full flex-1'>
                        {rightInfoItems.map((item: any) => (
                            <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                        ))}
                    </div>
                </div>
            </div>
            <div className='mt-3'>{noteContent}</div>
            {onEditRow && (
                <div className='flex items-center justify-end w-full pr-6 mt-2'>
                    <IconButton
                        onClick={() => onEditRow(row)}
                        size='small'
                        aria-label='edit row'
                        sx={{
                            backgroundColor: '#3F8EE8',
                            color: '#fff',
                            padding: '6px',
                            '&:hover': { backgroundColor: '#2364b5' },
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </div>
            )}
        </div>
    );

    const renderConfigure = () => (
        <div className='rounded-2xl bg-[#F5F7FB] min-h-[260px] flex flex-col p-2 pl-8'>
            <div className='text-base font-semibold text-[#2F2F2F]'>{t('Edit.configure')}</div>
            <div className='mt-3 grid grid-cols-2 gap-x-6 gap-y-3 flex-1'>
                <div className='flex flex-col space-y-2 items-start'>
                    {leftConfigureItems.map((item: any) => (
                        <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                    ))}
                </div>
                <div className='flex flex-col space-y-2 items-start h-full'>
                    <div className='flex flex-col space-y-2 items-start justify-start w-full flex-1'>
                        {rightConfigureItems.map((item: any) => (
                            <InfoColumnItem key={item.label} label={item.label} value={item.value} />
                        ))}
                    </div>
                </div>
            </div>
            {!ro && (
                <div className='flex items-center justify-end w-full pr-6 mt-2'>
                    <IconButton
                        onClick={() => onEditRow?.(row)}
                        size='small'
                        aria-label='edit row'
                        sx={{
                            backgroundColor: '#3F8EE8',
                            color: '#fff',
                            padding: '6px',
                            '&:hover': { backgroundColor: '#2364b5' },
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                </div>
            )}
        </div>
    );

    const renderRawData = () => (
        <div className='rounded-2xl bg-[#F5F7FB] p-4'>
            <TableContainer className='max-h-[240px] overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
                <Table size='small'>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#E7F0FA' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>{t('time')}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{t('temp')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {details.slice(0, 50).map((item: any) => (
                            <TableRow key={item.time}>
                                <TableCell>{item.time}</TableCell>
                                <TableCell>
                                    {item.temperature !== null && item.temperature !== ''
                                        ? `${item.temperature}${tempUnit === 'fahrenheit' ? '°F' : '°C'}`
                                        : '--'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );

    const renderTrend = () => (
        <div className='rounded-2xl bg-[#F5F7FB] p-4 '>
            <div className='mt-1'>
                <Plot
                    data={chartData.data}
                    layout={chartData.layout}
                    config={{ displayModeBar: false, responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className='flex justify-center py-6'>
                <CircularProgress />
            </div>
        );
    }

    const handleTouchStart = (event: any) => {
        setTouchStart(event.touches[0].clientX);
    };

    const handleTouchEnd = (event: any) => {
        if (touchStart === null) return;
        const deltaX = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(deltaX) > 50) {
            if (deltaX < 0 && pageIndex < 2) {
                onPageChange(pageIndex + 1);
            } else if (deltaX > 0 && pageIndex > 0) {
                onPageChange(pageIndex - 1);
            }
        }
        setTouchStart(null);
    };

    const handlePageIndicator = (index: any) => {
        onPageChange(index);
    };

    return (
        <div className='flex flex-col gap-4 bg-[#F5F7FB]'>
            <div className='relative overflow-hidden'>
                <div
                    className='flex transition-transform duration-300'
                    style={{ transform: `translateX(-${pageIndex * 100}%)` }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className='w-full flex-shrink-0 flex flex-col gap-4'>
                        <div
                            className='flex w-full overflow-hidden overflow-x-hidden items-stretch h-[50px]'
                            style={{
                                boxShadow:
                                    '0px 2px 2px rgba(0, 0, 0, 0.13), inset 0px 2px 2px rgba(255, 255, 255, 0.61)',
                            }}
                        >
                            <TabButton
                                label={t('Edit.information')}
                                active={activeTab === 'info'}
                                onClick={() => onTabChange('info')}
                                isLeft
                            />
                            <TabButton
                                label={t('Edit.configure')}
                                active={activeTab === 'configure'}
                                onClick={() => onTabChange('configure')}
                            />
                        </div>
                        {activeTab === 'info' ? renderInfo() : renderConfigure()}
                    </div>
                    <div className='w-full flex-shrink-0 flex flex-col gap-4'>
                        <PageHeader label={t('Edit.rawData')} />
                        {renderRawData()}
                    </div>
                    <div className='w-full flex-shrink-0 flex flex-col gap-4'>
                        <PageHeader label={t('trendGraph')} />
                        {renderTrend()}
                    </div>
                </div>
            </div>
            <div className='flex justify-center gap-4 pb-2'>
                {[0, 1, 2].map((idx: any) => (
                    <button
                        key={idx}
                        className={`h-1 rounded-full transition-all w-20 ${pageIndex === idx ? ' bg-[#3F8EE8]' : ' bg-[#D4D4D4]'}`}
                        onClick={() => handlePageIndicator(idx)}
                        type='button'
                        aria-label={`Page ${idx + 1}`}
                    />
                ))}
            </div>
        </div>
    );
}

export default MobileRecordDrawer;
